import { defineStore } from "pinia";
import { useQuery } from "@tanstack/vue-query";
import { request } from "@/axios";
import { computed, ref } from "vue";

interface SystemTimeResponse {
  time: number;
}

// 获取系统时间戳
export function getSystemTimestamp() {
  return request<SystemTimeResponse>({
    api: "/external/system",
    method: "get",
    notToast: true
  });
}

// 采用 vue-query 管理服务端时间，数据有效期默认 1 分钟（staleTime = 60000）
export const useSystemTimeStore = defineStore("systemTime", () => {
  const { data, refetch, dataUpdatedAt } = useQuery({
    queryKey: ["systemTime"],
    queryFn: async () => getSystemTimestamp(),
    staleTime: 60000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });

  const timestamp = computed(() => data.value?.data.time || Date.now());
  const inflight = ref<Promise<any> | null>(null);

  // 惰性获取：当数据在 ttl 内已更新过则不发请求；否则触发一次 refetch。
  // 并发去重：若已有进行中的请求，复用该 Promise，避免触发多次网络请求。
  const getServerTime = async (ttlMs = 600000): Promise<number> => {
    const updatedAt = dataUpdatedAt.value ?? 0; // ms
    const fresh = updatedAt && Date.now() - updatedAt < ttlMs;
    if (fresh && !!timestamp.value) return timestamp.value;

    if (inflight.value) {
      await inflight.value.catch(() => {});
      return timestamp.value;
    }

    inflight.value = refetch();
    await inflight.value.catch(() => {});
    inflight.value = null;
    return timestamp.value;
  };

  return {
    // 状态
    timestamp,

    getServerTime
  };
});
